import React, {
    useReducer, useRef, useEffect, useCallback
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import ProductActions from 'actions/ProductActions';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import Textarea from 'components/Inputs/Textarea/Textarea';
import Loader from 'components/Loader/Loader';
import SubmitMessage from 'components/ProductPage/QuestionsAndAnswers/SubmitMessage';
import ModalGuidelines from 'components/ProductPage/QuestionsAndAnswers/ModalGuidelines';
import UrlUtils from 'utils/Url';
import FormValidator from 'utils/FormValidator';
import Iovation from 'utils/Iovation';
import skuUtils from 'utils/Sku';
import Location from 'utils/Location';
import userUtils from 'utils/User';
import UI from 'utils/UI';
import localeUtils from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import questionAnswerApi from 'services/api/questionAndAnswer';
import questionsAnswersAnalytics from 'analytics/bindingMethods/pages/productPage/questionsAnswersBindings';
import {
    Text, Container, Link, Box, Grid, Button, Divider
} from 'components/ui';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import { PostLoad } from 'constants/events';
import Empty from 'constants/empty';
import { usePDPConfig } from 'components/ProductPage/hooks/usePDPConfig';
import QuestionsAndAnswersEN from 'components/ProductPage/QuestionsAndAnswers/locales/QuestionsAndAnswers_en_US';
import QuestionsAndAnswersFR from 'components/ProductPage/QuestionsAndAnswers/locales/QuestionsAndAnswers_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(QuestionsAndAnswersEN, QuestionsAndAnswersFR);

const MIN_QUESTION_CHARS = 15;
const MAX_QUESTION_CHARS = 255;

let initialLoadFired = false;
let emailId;

const initialState = {
    showGuidelines: false,
    emailNotification: true,
    presetLogin: '',
    isDisabled: false,
    acceptedTerms: false,
    showTermsError: false,
    questionBody: null,
    profileId: '',
    questionText: '',
    deviceData: null,
    requestSuccess: false,
    requestFailed: false,
    questionTextError: false,
    questionTextMessage: null
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_USER_DATA':
            return { ...state, presetLogin: action.presetLogin, profileId: action.profileId };
        case 'SET_DEVICE_DATA':
            return { ...state, deviceData: action.deviceData };
        case 'SET_DISABLED':
            return { ...state, isDisabled: action.value };
        case 'SET_QUESTION_TEXT_ERROR':
            return { ...state, questionTextError: true };
        case 'CLEAR_QUESTION_TEXT_ERROR':
            return { ...state, questionTextError: false, questionBody: action.questionBody };
        case 'SET_QUESTION_TEXT_MESSAGE':
            return { ...state, questionTextMessage: action.value, questionTextError: false };
        case 'SET_QUESTION_TEXT':
            return { ...state, questionText: action.value, questionTextError: false };
        case 'SET_EMAIL':
            return { ...state, emailNotification: action.checked, presetLogin: action.presetLogin };
        case 'SET_PRESET_LOGIN':
            return { ...state, presetLogin: action.value };
        case 'TOGGLE_GUIDELINES':
            return { ...state, showGuidelines: !state.showGuidelines };
        case 'SET_ACCEPTED_TERMS':
            return { ...state, acceptedTerms: action.value, showTermsError: false };
        case 'SET_TERMS_ERROR':
            return { ...state, showTermsError: true };
        case 'REQUEST_SUCCESS':
            return { ...state, requestSuccess: true, requestFailed: false };
        case 'REQUEST_FAILED':
            return { ...state, requestFailed: true, requestSuccess: false };
        default:
            return state;
    }
};

const SubmitQuestionBody = () => {
    const [state, localDispatch] = useReducer(reducer, initialState);
    const reduxDispatch = useDispatch();
    const topContainerRef = useRef(null);
    const questionTextRef = useRef(null);
    const emailTextRef = useRef(null);
    const currentProduct = useSelector(state_ => state_.page.product, shallowEqual);
    const user = useSelector(state_ => state_.user, shallowEqual);
    const userRef = useRef(user);
    userRef.current = user;
    const { isCommunityServiceEnabled } = usePDPConfig();

    useEffect(() => {
        if (currentProduct?.currentSku?.actionFlags && !initialLoadFired) {
            questionsAnswersAnalytics.fireSubmitPageLoad(currentProduct, true);
            initialLoadFired = true;
        }
    }, [currentProduct]);

    useEffect(() => {
        const productId = UrlUtils.getParamsByName('productId');
        const skuId = UrlUtils.getParamsByName('skuId');
        reduxDispatch(ProductActions.fetchCurrentProduct(productId, skuId));
        Sephora.Util.onLastLoadEvent(window, [PostLoad], () => {
            Iovation.loadIovationScript();
            Iovation.getBlackboxString([], true).then(blackBoxString => {
                localDispatch({ type: 'SET_DEVICE_DATA', deviceData: blackBoxString });
            });
            const currentUser = userRef.current;
            const isAnonymous = userUtils.isAnonymous();

            if (currentUser) {
                localDispatch({
                    type: 'SET_USER_DATA',
                    presetLogin: !isAnonymous ? currentUser.login : null,
                    profileId: !isAnonymous ? currentUser.profileId : null
                });
                emailId = !isAnonymous ? currentUser.login : null;
            }
        });
    }, []);

    const redirectToPpage = useCallback(() => Location.setLocation(`/product/${currentProduct.productDetails.productId}#QandA`), [currentProduct]);

    const getProductTargetUrl = useCallback(() => {
        const { skuId } = UrlUtils.getParams();

        return `${currentProduct.targetUrl}${skuId ? '?skuId=' + skuId : ''}`;
    }, [currentProduct]);

    const validateQuestionText = useCallback(() => {
        const { questionText } = state;

        if (FormValidator.isEmpty(questionText)) {
            localDispatch({ type: 'SET_QUESTION_TEXT_ERROR' });

            return getText('enterQuestion');
        }

        if (questionText.length < MIN_QUESTION_CHARS) {
            localDispatch({ type: 'SET_QUESTION_TEXT_ERROR' });

            return getText('errorMessageText', [MIN_QUESTION_CHARS]);
        }

        localDispatch({ type: 'CLEAR_QUESTION_TEXT_ERROR', questionBody: questionText });

        return null;
    }, [state.questionText]);

    const validateEmailText = useCallback(
        emailText => {
            if (!state.emailNotification) {
                return null;
            }

            if (FormValidator.isEmpty(emailText)) {
                return getText('emptyEmailMessage');
            }

            if (!FormValidator.isValidEmailAddress(emailText)) {
                return getText('invalidEmailMessage');
            }

            return null;
        },
        [state.emailNotification]
    );

    const setEmail = useCallback(checked => {
        localDispatch({ type: 'SET_EMAIL', checked, presetLogin: checked ? emailId : '' });

        if (checked === false && emailTextRef.current) {
            emailTextRef.current.validateError();
        }
    }, []);

    const handleQuestionTextChange = useCallback(
        questionText => {
            const msg = questionText.length === MAX_QUESTION_CHARS ? getText('maxCharacter') : null;

            if (msg || state.questionTextMessage) {
                localDispatch({ type: 'SET_QUESTION_TEXT_MESSAGE', value: msg });
            } else {
                localDispatch({ type: 'SET_QUESTION_TEXT', value: questionText });
            }
        },
        [state.questionTextMessage]
    );

    const validateForm = useCallback(() => {
        const errors = FormValidator.getErrors([questionTextRef.current, emailTextRef.current]);
        let formIsValid = true;

        if (!state.acceptedTerms) {
            formIsValid = false;
            localDispatch({ type: 'SET_TERMS_ERROR' });
        }

        if (errors.fields.length) {
            formIsValid = false;
            questionsAnswersAnalytics.fireSubmitLinkTrackingError(errors.messages);
            UI.scrollTo({ ref: topContainerRef });
        }

        return formIsValid;
    }, [state.acceptedTerms]);

    const submitQuestion = useCallback(
        e => {
            e.preventDefault();
            localDispatch({ type: 'SET_DISABLED', value: true });

            if (validateForm()) {
                const { profileId } = state;
                const productDetails = currentProduct.productDetails;
                const productId = productDetails.productId;
                const isUS = localeUtils.isUS();
                const locale = localeUtils.isCanada() ? (localeUtils.isFrench() ? 'fr-CA' : 'en-CA') : 'en-US';
                const emailText = emailTextRef.current ? emailTextRef.current.getValue() : Empty.string;

                questionAnswerApi
                    .submitQuestion(
                        productId,
                        encodeURIComponent(state.questionText.replace(/%\d\w|\+/g, ' ')),
                        state.deviceData,
                        emailText,
                        isCommunityServiceEnabled,
                        locale,
                        profileId
                    )
                    .then(() => {
                        questionsAnswersAnalytics.fireSubmitSuccessPageLoad(currentProduct, true);
                        localDispatch({ type: 'REQUEST_SUCCESS' });
                        UI.scrollTo({ ref: topContainerRef });

                        const sku = currentProduct.currentSku;
                        const questionBodyData = {
                            questionBody: state.questionBody,
                            questionSkuId: sku?.skuId || null,
                            questionProductId: productDetails?.productId,
                            questionPrice: sku?.listPrice,
                            questionCurrency: isUS ? 'USD' : 'CAD',
                            questionBrandName: productDetails?.brand?.displayName,
                            questionProductName: productDetails?.displayName,
                            questionVariationType: sku?.variationType,
                            questionVariationValue: sku?.variationValue,
                            questionProductTopCategory: currentProduct?.parentCategory?.displayName
                        };
                        pdpAnalytics.trackEvent(
                            anaConsts.LINK_TRACKING_EVENT,
                            {
                                data: {
                                    linkName: anaConsts.EVENT_NAMES.ASK_QUESTION_BODY_DETAILS,
                                    actionInfo: anaConsts.EVENT_NAMES.ASK_QUESTION_BODY_DETAILS,
                                    specificEventName: anaConsts.EVENT_NAMES.ASK_QUESTION_BODY_DETAILS,
                                    sponsoredProductInformation: questionBodyData
                                }
                            },
                            { specificEventName: anaConsts.EVENT_NAMES.ASK_QUESTION_BODY_DETAILS }
                        );
                    })
                    .catch(error => {
                        const errorMessages =
                            error && error.errorMessages && error.errorMessages.length
                                ? error.errorMessages
                                : ['Bazaarvoice returned error(s) during Question Submit'];
                        questionsAnswersAnalytics.fireSubmitLinkTrackingError(errorMessages);
                        localDispatch({ type: 'REQUEST_FAILED' });
                        UI.scrollTo({ ref: topContainerRef });
                    });
            } else {
                localDispatch({ type: 'SET_DISABLED', value: false });
            }
        },
        [state, currentProduct, isCommunityServiceEnabled, validateForm]
    );

    const { requestSuccess, requestFailed, showTermsError } = state;
    const currentSku = currentProduct?.currentSku;
    const productDetails = currentProduct?.productDetails || {};
    const cancelGoBackUrl = getProductTargetUrl();

    return (
        <>
            <Container
                paddingTop={[4, 6]}
                ref={topContainerRef}
            >
                {currentSku && !requestSuccess && !requestFailed && (
                    <form
                        noValidate
                        onSubmit={submitQuestion}
                    >
                        <Grid
                            marginBottom={[5, 6]}
                            gap={3}
                            columns='1fr auto'
                            alignItems='baseline'
                            lineHeight='tight'
                        >
                            <Text
                                is='h1'
                                fontSize={['md', 'lg']}
                                fontWeight='bold'
                                children={getText('askAQuestionTitle')}
                            />
                            <Link
                                display={[null, 'none']}
                                href={currentProduct.targetUrl}
                                color='blue'
                                padding={2}
                                margin={-2}
                                data-at={Sephora.debug.dataAt('cancel_link')}
                                children={getText('cancel')}
                            />
                        </Grid>
                        <Grid
                            columns='auto 1fr'
                            gap={4}
                            lineHeight='tight'
                            alignItems='center'
                        >
                            <ProductImage
                                id={currentSku.skuId}
                                skuImages={currentSku.skuImages}
                                size={100}
                                disableLazyLoad={true}
                            />
                            <div>
                                <h2>
                                    <Text
                                        display='block'
                                        fontWeight='bold'
                                        data-at={Sephora.debug.dataAt('brand_name')}
                                        children={productDetails.brand?.displayName}
                                    />
                                    <Text
                                        fontSize={['base', 'md']}
                                        data-at={Sephora.debug.dataAt('product_name')}
                                        children={productDetails.displayName}
                                    />
                                </h2>
                                <Text
                                    is='p'
                                    fontSize={['sm', 'base']}
                                    marginTop={['.5em', '1em']}
                                    css={{ ':empty': { marginTop: 0 } }}
                                >
                                    {currentSku.refinements && currentSku.refinements.sizeRefinements && (
                                        <span>
                                            {currentSku.refinements.sizeRefinements[0]} {getText('size')}
                                        </span>
                                    )}
                                    {currentProduct.variationType !== skuUtils.skuVariationType.NONE && currentSku.variationValue && (
                                        <Text
                                            display='block'
                                            marginTop='.125em'
                                            data-at={Sephora.debug.dataAt('variation_label')}
                                        >
                                            {currentSku.variationValue}
                                            {currentSku.variationDesc && ` - ${currentSku.variationDesc}`}
                                        </Text>
                                    )}
                                </Text>
                            </div>
                        </Grid>
                        <Divider marginY={[5, 6]} />
                        <Box maxWidth={612}>
                            <Textarea
                                label={getText('enterYourQuestion', [MAX_QUESTION_CHARS])}
                                labelDataAt={'question_field_ghost_text'}
                                placeholder={getText('exampleQuestion')}
                                rows={4}
                                name='reviewQuestion'
                                maxLength={MAX_QUESTION_CHARS}
                                charCountDataAt={'char_counter_text'}
                                handleChange={handleQuestionTextChange}
                                invalid={state.questionTextError}
                                errorDataAt={'error_msg_text'}
                                warning={state.questionTextMessage}
                                validate={validateQuestionText}
                                ref={questionTextRef}
                                data-at={Sephora.debug.dataAt('question_field')}
                            />
                            <Link
                                padding={2}
                                margin={-2}
                                onClick={() => localDispatch({ type: 'TOGGLE_GUIDELINES' })}
                                color='blue'
                                data-at={Sephora.debug.dataAt('guidelines_link')}
                                children={getText('questionAnswerGuidelines')}
                            />
                            <Checkbox
                                paddingY={null}
                                marginBottom={3}
                                marginTop={5}
                                checked={!!state.emailNotification}
                                data-at={Sephora.debug.dataAt('work_at_seph_checkbox')}
                                onClick={e => setEmail(e.target.checked)}
                            >
                                {getText('emailNotification')}
                            </Checkbox>
                            <InputEmail
                                id='emailSignUpInput'
                                placeholder={getText('emailAddress')}
                                disabled={!state.emailNotification}
                                login={state.presetLogin}
                                name='email'
                                isInputChecked={state.emailNotification}
                                onChange={event => localDispatch({ type: 'SET_PRESET_LOGIN', value: event.target.value })}
                                ref={emailTextRef}
                                marginBottom={2}
                                validate={() => validateEmailText(state.presetLogin)}
                            />
                            <Text
                                is='p'
                                lineHeight='tight'
                                fontSize='sm'
                                color='gray'
                                children={`${getText('notification')}`}
                            />
                        </Box>
                        <Divider
                            marginTop={[3, 4]}
                            marginBottom={[3, 5]}
                        />
                        <Grid
                            gap={2}
                            maxWidth={612}
                        >
                            <Checkbox
                                color={showTermsError ? 'error' : null}
                                paddingY={null}
                                checked={!!state.acceptedTerms}
                                onClick={e => localDispatch({ type: 'SET_ACCEPTED_TERMS', value: e.target.checked })}
                            >
                                {getText('yesIAgree')}
                                <Link
                                    href='https://www.sephora.com/beauty/terms-of-use'
                                    color='blue'
                                    data-at={Sephora.debug.dataAt('terms_and_conditions_link')}
                                    padding={2}
                                    margin={-2}
                                    target='_blank'
                                >
                                    {getText('termsAndConditions')}
                                </Link>
                                .
                            </Checkbox>
                            {showTermsError && (
                                <Text
                                    is='p'
                                    color='error'
                                    paddingLeft={6}
                                    fontSize='sm'
                                    lineHeight='tight'
                                >
                                    {getText('termsError')}
                                </Text>
                            )}
                            <Text
                                is='p'
                                fontSize='sm'
                                paddingLeft={6}
                                marginBottom={1}
                                lineHeight='tight'
                            >
                                <Text
                                    display='inline-block'
                                    marginTop='.125em'
                                    fontWeight='bold'
                                >
                                    {getText('noteLabel')}:{' '}
                                </Text>
                                {getText('responsiblity')}
                            </Text>
                            <Text
                                is='p'
                                lineHeight='tight'
                                fontSize='sm'
                                color='gray'
                                children={`${getText('note')}`}
                            />
                            <Grid
                                gap={4}
                                alignItems='center'
                                columns={[null, 'auto 1fr']}
                            >
                                <Button
                                    type='submit'
                                    disabled={state.isDisabled}
                                    width={['100%', 'auto']}
                                    minWidth={[null, '14.5em']}
                                    variant='primary'
                                    children={getText('submit')}
                                    data-at={Sephora.debug.dataAt('submit_question_button')}
                                />
                                <Link
                                    display={['none', 'block']}
                                    href={cancelGoBackUrl}
                                    color='blue'
                                    padding={2}
                                    margin={-2}
                                    marginRight='auto'
                                    data-at={Sephora.debug.dataAt('cancel_link')}
                                    children={getText('cancel')}
                                />
                            </Grid>
                        </Grid>
                    </form>
                )}
                {!currentSku && <Loader isShown={true} />}
                {(requestSuccess || requestFailed) && (
                    <SubmitMessage
                        isQuestion={true}
                        isError={requestFailed}
                        currentSku={currentSku}
                        redirectTo={redirectToPpage}
                        currentProduct={currentProduct}
                    />
                )}
            </Container>
            {state.showGuidelines && <ModalGuidelines onDismiss={() => localDispatch({ type: 'TOGGLE_GUIDELINES' })} />}
        </>
    );
};

export default wrapFunctionalComponent(SubmitQuestionBody, 'SubmitQuestionBody');
